import { createApi } from '@reduxjs/toolkit/query/react';

import {
  BPOSummaryResponse,
  BulkScheduleRequest,
  BulkScheduleResponse,
  CancelOutreachRequest,
  CancelOutreachResponse,
  CommandCenterStats,
  EnhancedBlandWorkflowsResponse,
  MassSchedulerResult,
  MedicalRecordsBPOSummaryResponse,
  PoliceOutreachSummary,
  PoliceSchedulerResult,
  ScheduleOutreachRequest,
  ScheduleOutreachResponse,
  TriggerOutreachResponse,
  VoiceAgentCommandCenterResponse,
  VoiceAIAgentRunsResponse,
} from '@/types/enhanced-bland-call';

import { baseQueryWithErrorHandling } from './baseQuery';

type StartEnhancedBlandCallWorkflowResponse = {
  message: string;
  workflow_id: string;
  task_id: string;
  terminated_existing?: boolean;
};

type RetryEnhancedBlandCallWorkflowResponse = {
  message: string;
  task_id: string;
};

export const blandApiClient = createApi({
  reducerPath: 'blandApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'EnhancedBlandWorkflows',
    'GlobalBlandWorkflows',
    'CommandCenter',
    'CommandCenterStats',
    'TaskRuns',
    'PoliceOutreach',
    'SchedulerWorkflows',
  ],
  endpoints: (builder) => ({
    // BPO summary (Voice AI BPO actions) endpoint - coverage type
    getBpoSummary: builder.query<BPOSummaryResponse, { start: string }>({
      query: ({ start }) => {
        const search = new URLSearchParams({ start, type: 'coverage' });
        return `api/voice-ai/bpo-summary/?${search.toString()}`;
      },
      providesTags: ['CommandCenter'],
      keepUnusedDataFor: 10,
    }),
    // Medical Records BPO summary endpoint
    getMedicalRecordsBpoSummary: builder.query<
      MedicalRecordsBPOSummaryResponse,
      { start: string }
    >({
      query: ({ start }) => {
        const search = new URLSearchParams({ start, type: 'medical_records' });
        return `api/voice-ai/bpo-summary/?${search.toString()}`;
      },
      providesTags: ['CommandCenter'],
      keepUnusedDataFor: 10,
    }),
    // Enhanced Bland Call workflows endpoints
    getEnhancedBlandCallWorkflows: builder.query<
      EnhancedBlandWorkflowsResponse,
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/bland/enhanced-workflows/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'EnhancedBlandWorkflows', id: matterId },
        { type: 'EnhancedBlandWorkflows', id: 'LIST' },
      ],
      // Skip cache if we have active workflows
      keepUnusedDataFor: 0,
    }),

    // Global Enhanced Bland Call workflows endpoint - COMMAND CENTER!
    getGlobalEnhancedBlandCallWorkflows: builder.query<
      EnhancedBlandWorkflowsResponse,
      void
    >({
      query: () => `api/bland/enhanced-workflows/global/`,
      providesTags: ['GlobalBlandWorkflows'],
      keepUnusedDataFor: 0,
    }),

    // Enhanced Command Center endpoints
    getCommandCenterData: builder.query<
      VoiceAgentCommandCenterResponse,
      Record<string, string | number | boolean> | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            searchParams.set(key, String(value));
          });
        }
        const queryString = searchParams.toString();
        return `api/bland/command-center/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['CommandCenter'],
      keepUnusedDataFor: 30, // Cache for 30 seconds
    }),

    getCommandCenterStats: builder.query<CommandCenterStats, void>({
      query: () => `api/bland/command-center/stats/`,
      providesTags: ['CommandCenterStats'],
      keepUnusedDataFor: 10, // Cache for 10 seconds
    }),

    // Get all runs for a specific task
    getTaskRuns: builder.query<
      VoiceAIAgentRunsResponse,
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/bland/enhanced-workflows/${taskId}/runs/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'TaskRuns', id: taskId },
      ],
      keepUnusedDataFor: 30, // Cache for 30 seconds since this is historical data
    }),

    startEnhancedBlandCallWorkflow: builder.mutation<
      StartEnhancedBlandCallWorkflowResponse,
      {
        matterId: string;
        taskId: string;
        preferredPhoneNumber?: string;
        maxCallsOverride?: number;
        attachmentBlobIds?: string[];
        /** Use AI Hold & Transfer mode: AI waits on hold, transfers to user when human answers */
        useHoldAndTransfer?: boolean;
        /** Optional override for the transfer phone number (instead of auto-lookup) */
        transferToPhoneOverride?: string;
      }
    >({
      query: ({
        matterId,
        taskId,
        preferredPhoneNumber,
        maxCallsOverride,
        attachmentBlobIds,
        useHoldAndTransfer,
        transferToPhoneOverride,
      }) => ({
        url: `api/matters/${matterId}/bland/enhanced-workflows/start/`,
        method: 'POST',
        body: {
          task_id: taskId,
          ...(preferredPhoneNumber
            ? { preferred_phone_number: preferredPhoneNumber }
            : {}),
          ...(typeof maxCallsOverride === 'number'
            ? { max_calls_override: maxCallsOverride }
            : {}),
          ...(attachmentBlobIds && attachmentBlobIds.length > 0
            ? { attachment_blob_ids: attachmentBlobIds }
            : {}),
          ...(useHoldAndTransfer ? { use_hold_and_transfer: true } : {}),
          ...(transferToPhoneOverride
            ? { transfer_to_phone_override: transferToPhoneOverride }
            : {}),
        },
      }),
      invalidatesTags: (result, error, { matterId, taskId }) => [
        { type: 'EnhancedBlandWorkflows', id: matterId },
        { type: 'EnhancedBlandWorkflows', id: 'LIST' },
        { type: 'TaskRuns', id: taskId },
        'GlobalBlandWorkflows',
        'CommandCenter',
        'CommandCenterStats',
      ],
      // Optimistic update - assume success and refetch immediately
      onQueryStarted: async ({ matterId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Force refetch after a brief delay to ensure backend is updated
          setTimeout(() => {
            dispatch(
              blandApiClient.util.invalidateTags([
                { type: 'EnhancedBlandWorkflows', id: matterId },
                'CommandCenter',
              ]),
            );
          }, 1000);
        } catch {
          // Error handling is done by the component
        }
      },
    }),

    retryEnhancedBlandCallWorkflow: builder.mutation<
      RetryEnhancedBlandCallWorkflowResponse,
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) => ({
        url: `api/matters/${matterId}/bland/enhanced-workflows/${taskId}/retry/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, taskId }) => [
        { type: 'EnhancedBlandWorkflows', id: matterId },
        { type: 'EnhancedBlandWorkflows', id: 'LIST' },
        { type: 'TaskRuns', id: taskId },
        'GlobalBlandWorkflows',
        'CommandCenter',
        'CommandCenterStats',
      ],
      onQueryStarted: async ({ matterId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Force refetch after retry
          setTimeout(() => {
            dispatch(
              blandApiClient.util.invalidateTags([
                { type: 'EnhancedBlandWorkflows', id: matterId },
                'CommandCenter',
              ]),
            );
          }, 500);
        } catch {
          // Error handling is done by the component
        }
      },
    }),

    terminateEnhancedBlandCallWorkflow: builder.mutation<
      { message: string; task_id: string; workflow_id: string },
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) => ({
        url: `api/matters/${matterId}/bland/enhanced-workflows/${taskId}/terminate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, taskId }) => [
        { type: 'EnhancedBlandWorkflows', id: matterId },
        { type: 'EnhancedBlandWorkflows', id: 'LIST' },
        { type: 'TaskRuns', id: taskId },
        'GlobalBlandWorkflows',
        'CommandCenter',
        'CommandCenterStats',
      ],
      onQueryStarted: async ({ matterId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Force refetch after termination
          setTimeout(() => {
            dispatch(
              blandApiClient.util.invalidateTags([
                { type: 'EnhancedBlandWorkflows', id: matterId },
                'CommandCenter',
              ]),
            );
          }, 500);
        } catch {
          // Error handling is done by the component
        }
      },
    }),

    refreshEnhancedBlandCallWorkflowStatus: builder.mutation<
      {
        message: string;
        task_id: string;
        workflow_id: string;
        status: string;
        calls_made: number;
        is_ready: boolean;
      },
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) => ({
        url: `api/matters/${matterId}/bland/enhanced-workflows/${taskId}/refresh/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId, taskId }) => [
        { type: 'EnhancedBlandWorkflows', id: matterId },
        { type: 'EnhancedBlandWorkflows', id: 'LIST' },
        { type: 'TaskRuns', id: taskId },
        'GlobalBlandWorkflows',
        'CommandCenter',
        'CommandCenterStats',
      ],
      onQueryStarted: async ({ matterId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Force refetch after refresh
          dispatch(
            blandApiClient.util.invalidateTags([
              { type: 'EnhancedBlandWorkflows', id: matterId },
              'CommandCenter',
            ]),
          );
        } catch {
          // Error handling is done by the component
        }
      },
    }),

    // Police Outreach Scheduling endpoints
    getPoliceOutreachSummary: builder.query<
      PoliceOutreachSummary,
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/police-reports/outreach/summary/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'PoliceOutreach', id: `summary-${matterId}` },
      ],
      keepUnusedDataFor: 30, // Cache for 30 seconds
    }),

    schedulePoliceOutreach: builder.mutation<
      ScheduleOutreachResponse,
      ScheduleOutreachRequest
    >({
      query: ({ matter_id, police_report_id, when, force }) => ({
        url: `api/matters/${matter_id}/police/outreach/schedule/`,
        method: 'POST',
        body: { police_report_id, when, force },
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'PoliceOutreach', id: `summary-${matter_id}` },
        { type: 'EnhancedBlandWorkflows', id: matter_id },
        'GlobalBlandWorkflows',
      ],
    }),

    bulkSchedulePoliceOutreach: builder.mutation<
      BulkScheduleResponse,
      BulkScheduleRequest
    >({
      query: ({ matter_id, schedules }) => ({
        url: `api/matters/${matter_id}/police/outreach/bulk-schedule/`,
        method: 'POST',
        body: { schedules },
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'PoliceOutreach', id: `summary-${matter_id}` },
        { type: 'EnhancedBlandWorkflows', id: matter_id },
        'GlobalBlandWorkflows',
      ],
    }),

    cancelPoliceOutreach: builder.mutation<
      CancelOutreachResponse,
      CancelOutreachRequest & { matterId: string }
    >({
      query: ({ run_id, reason, matterId }) => ({
        url: `api/police/outreach/${run_id}/cancel/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'PoliceOutreach', id: `summary-${matterId}` },
        { type: 'EnhancedBlandWorkflows', id: matterId },
        'GlobalBlandWorkflows',
      ],
    }),

    triggerPoliceOutreach: builder.mutation<
      TriggerOutreachResponse,
      { runId: string; matterId: string }
    >({
      query: ({ runId }) => ({
        url: `api/police/outreach/${runId}/trigger/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'PoliceOutreach', id: `summary-${matterId}` },
        { type: 'EnhancedBlandWorkflows', id: matterId },
        'GlobalBlandWorkflows',
      ],
    }),

    // Workflow Management endpoints
    triggerPoliceScheduler: builder.mutation<
      PoliceSchedulerResult,
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/police-reports/scheduler/trigger/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'PoliceOutreach', id: `summary-${matterId}` },
        { type: 'SchedulerWorkflows', id: matterId },
        'CommandCenter',
        'CommandCenterStats',
      ],
    }),

    triggerMassPoliceScheduler: builder.mutation<MassSchedulerResult, void>({
      query: () => ({
        url: `api/police/scheduler/mass-trigger/`,
        method: 'POST',
      }),
      invalidatesTags: ['SchedulerWorkflows', 'PoliceOutreach'],
    }),

    getSchedulerWorkflowStatus: builder.query<
      PoliceSchedulerResult,
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/police/scheduler/status/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'SchedulerWorkflows', id: matterId },
      ],
      keepUnusedDataFor: 10, // Short cache for status checks
    }),

    getMassSchedulerStatus: builder.query<MassSchedulerResult, void>({
      query: () => `api/police/scheduler/mass-status/`,
      providesTags: ['SchedulerWorkflows'],
      keepUnusedDataFor: 10,
    }),

    // Dev test scenarios endpoints (non-production only)
    getDevTestScenarios: builder.query<
      {
        scenarios: Array<{
          name: string;
          number: string;
          description: string;
          behavior: string;
        }>;
        is_dev_environment: boolean;
      },
      void
    >({
      query: () => `api/bland/dev/test-scenarios/`,
      providesTags: ['CommandCenter'],
      keepUnusedDataFor: 60, // Cache for 1 minute
    }),

    routeDevCall: builder.mutation<
      {
        message: string;
        workflow_id: string;
        test_scenario: string;
        routed_to: string;
        description: string;
        behavior: string;
      },
      { workflowId: string; testScenario: string }
    >({
      query: ({ workflowId, testScenario }) => ({
        url: `api/bland/dev/route-call/`,
        method: 'POST',
        body: {
          workflow_id: workflowId,
          test_scenario: testScenario,
        },
      }),
      invalidatesTags: ['CommandCenter', 'GlobalBlandWorkflows'],
    }),
  }),
});

export const {
  useGetEnhancedBlandCallWorkflowsQuery,
  useGetGlobalEnhancedBlandCallWorkflowsQuery,
  useGetCommandCenterDataQuery,
  useGetCommandCenterStatsQuery,
  useGetTaskRunsQuery,
  useStartEnhancedBlandCallWorkflowMutation,
  useRetryEnhancedBlandCallWorkflowMutation,
  useTerminateEnhancedBlandCallWorkflowMutation,
  useRefreshEnhancedBlandCallWorkflowStatusMutation,
  // Police Outreach Scheduling hooks
  useGetPoliceOutreachSummaryQuery,
  useSchedulePoliceOutreachMutation,
  useBulkSchedulePoliceOutreachMutation,
  useCancelPoliceOutreachMutation,
  useTriggerPoliceOutreachMutation,
  // Workflow Management hooks
  useTriggerPoliceSchedulerMutation,
  useTriggerMassPoliceSchedulerMutation,
  useGetSchedulerWorkflowStatusQuery,
  useGetMassSchedulerStatusQuery,
  // Dev test scenario hooks
  useGetDevTestScenariosQuery,
  useRouteDevCallMutation,
  // BPO Summary hooks
  useGetBpoSummaryQuery,
  useGetMedicalRecordsBpoSummaryQuery,
} = blandApiClient;
